import {
  Banner,
  Button,
  Buttons,
  Container,
  Header,
  Layout,
  Level,
  LevelGroup,
  LevelItem,
  MainContent,
  Section,
  Text,
} from '@inmotionnow/momentum-components-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import {
  type AccountUser,
  useAccountUsers,
} from '@/features/accounts/accountApi'
import { userTypeValueToId } from '@/features/users/user-utils'
import { UserTypeId } from '@/features/users/userApi'

import { AccountMembersList } from './account-members-list'
import { MemberSearchToolbar } from './member-search-toolbar'

export type IAccountMembersListViewComponentProps = {}

const ROLE_FILTER_ALL = -1
type RoleFilter = UserTypeId | typeof ROLE_FILTER_ALL

const matchesSearch = (accountUser: AccountUser, term: string): boolean => {
  if (!term) return true
  const lower = term.toLowerCase()
  return (
    accountUser.display_name.toLowerCase().includes(lower) ||
    accountUser.email.toLowerCase().includes(lower)
  )
}

const matchesRole = (
  accountUser: AccountUser,
  roleFilter: RoleFilter,
): boolean => {
  if (roleFilter === ROLE_FILTER_ALL) return true
  return userTypeValueToId(accountUser.user_type) === roleFilter
}

const AccountMembersListView = (
  _props: IAccountMembersListViewComponentProps,
) => {
  const { t } = useTranslation()
  const { accountId } = useParams<{ accountId: string }>()
  const navigate = useNavigate()
  const bannerTitle = t('ACCOUNT_MEMBERS')

  const currentAccountUID = (() => {
    if (!accountId) return undefined
    const parsed = Number.parseInt(accountId, 10)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined
  })()

  const {
    data: accountUsersData,
    isLoading,
    isError,
  } = useAccountUsers(currentAccountUID)

  const [searchTerm, setSearchTerm] = React.useState('')
  const [roleFilter, setRoleFilter] =
    React.useState<RoleFilter>(ROLE_FILTER_ALL)

  const statusLayout = (message: string) => (
    <Layout>
      <Header>
        <Banner title={bannerTitle} />
      </Header>
      <MainContent>
        <Section>
          <Container size='lg'>
            <Text>{message}</Text>
          </Container>
        </Section>
      </MainContent>
    </Layout>
  )

  if (!currentAccountUID) return statusLayout(t('ACCOUNT_NOT_FOUND_MESSAGE'))
  if (isLoading) return statusLayout(t('LOADING'))
  if (isError) return statusLayout(t('ACCOUNT_NOT_FOUND_MESSAGE'))

  const accountUsers = accountUsersData ?? []
  const searchFiltered = accountUsers.filter(m => matchesSearch(m, searchTerm))

  const adminMembers = searchFiltered.filter(m =>
    matchesRole(m, UserTypeId.Admin),
  )
  const teamMembers = searchFiltered.filter(m =>
    matchesRole(m, UserTypeId.TeamMember),
  )
  const filteredMembers = searchFiltered.filter(m => matchesRole(m, roleFilter))

  const noResultsMessage = !accountUsers.length
    ? t('ACCOUNT_MEMBERS_EMPTY')
    : t('ACCOUNT_MEMBERS_NO_RESULTS')

  const allAdminMembers = accountUsers.filter(m =>
    matchesRole(m, UserTypeId.Admin),
  )
  const allTeamMembers = accountUsers.filter(m =>
    matchesRole(m, UserTypeId.TeamMember),
  )
  const adminEmptyMessage = !allAdminMembers.length
    ? t('ACCOUNT_ADMINS_EMPTY')
    : t('ACCOUNT_ADMINS_NO_RESULTS')
  const teamMemberEmptyMessage = !allTeamMembers.length
    ? t('ACCOUNT_TEAM_MEMBERS_EMPTY')
    : t('ACCOUNT_TEAM_MEMBERS_NO_RESULTS')
  const filteredEmptyMessage =
    roleFilter === UserTypeId.Admin ? adminEmptyMessage : teamMemberEmptyMessage

  const roleFilterOptions = [
    { value: ROLE_FILTER_ALL, displayText: t('ALL') },
    { value: UserTypeId.Admin, displayText: t('ADMIN') },
    { value: UserTypeId.TeamMember, displayText: t('TEAM_MEMBER') },
  ]

  // roleFilter is always one of the three values in roleFilterOptions, so find always matches
  const selectedRoleLabel = roleFilterOptions.find(
    opt => opt.value === roleFilter,
  )!.displayText

  return (
    <Layout>
      <Header>
        <Banner title={bannerTitle} />
        <Level>
          <LevelGroup position='right'>
            <LevelItem>
              <Buttons>
                <Button
                  label={t('CREATE_USER')}
                  onClick={() =>
                    navigate(`/user/create?accountId=${currentAccountUID}`)
                  }
                />
              </Buttons>
            </LevelItem>
          </LevelGroup>
        </Level>
      </Header>
      <MainContent>
        <Section padding={['lg', 'none', 'none', 'none']}>
          <Container size='lg'>
            <Section padding={['none', 'none', 'lg', 'none']}>
              <MemberSearchToolbar
                searchTerm={searchTerm}
                searchPlaceholder={t('SEARCH_MEMBERS_PLACEHOLDER')}
                onSearchChange={setSearchTerm}
                roleFilter={roleFilter}
                onRoleFilterChange={setRoleFilter}
                roleFilterOptions={roleFilterOptions}
              />
            </Section>
            {roleFilter === ROLE_FILTER_ALL ? (
              <>
                {searchFiltered.length ? (
                  <>
                    <AccountMembersList
                      title={`${t('ADMIN')} (${adminMembers.length})`}
                      members={adminMembers}
                      emptyMessage={adminEmptyMessage}
                      accountId={currentAccountUID}
                    />
                    <AccountMembersList
                      title={`${t('TEAM_MEMBER')} (${teamMembers.length})`}
                      members={teamMembers}
                      emptyMessage={teamMemberEmptyMessage}
                      accountId={currentAccountUID}
                    />
                  </>
                ) : (
                  <Text dataTestingId='account-members-empty'>
                    {noResultsMessage}
                  </Text>
                )}
              </>
            ) : (
              <AccountMembersList
                title={`${selectedRoleLabel} (${filteredMembers.length})`}
                members={filteredMembers}
                emptyMessage={filteredEmptyMessage}
                accountId={currentAccountUID}
              />
            )}
          </Container>
        </Section>
      </MainContent>
    </Layout>
  )
}

export { AccountMembersListView }
